import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';

import { useRoomType } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { RoomType as RoomTypeEnum } from 'proto/Offer/Room/RoomType';

import Segment from '../Segment';
import { config } from '../Segment/config';
import FilterLabel from '../Segment/FilterLabel';
import { FilterProps } from '../types';

import {
    buildRoomTypeLabel,
    buildRoomTypeTooltip,
} from './__helper/roomTypeLabel';

import styles from './RoomType.module.scss';

const roomTypeArray: RoomTypeEnum[] = [
    RoomTypeEnum.SingleBedRoom,
    RoomTypeEnum.DoubleBedRoom,
    RoomTypeEnum.FamilyRoom,
    RoomTypeEnum.TripleBedRoom,
    RoomTypeEnum.MultiShareRoom,
    RoomTypeEnum.Studio,
    RoomTypeEnum.Apartment,
    RoomTypeEnum.DuplexBedRoom,
    RoomTypeEnum.Bungalow,
    RoomTypeEnum.Suite,
    RoomTypeEnum.Villa,
];

const RoomType = ({ isSticky = false }: FilterProps) => {
    const { t } = useTranslation();
    const [accommodationMeta] = useAccommodationMeta();
    const [roomTypeList, setRoomTypeList] = useRoomType();
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();

    const onSelect = useCallback(
        (selection: RoomTypeEnum) => {
            onToggle(selection);
            setRoomTypeList((current) => toggleValueInSet(current, selection));
        },
        [onToggle, setRoomTypeList]
    );
    const filterList = roomTypeArray.map((roomType) => {
        const isChecked = roomTypeList.has(roomType);
        const label = buildRoomTypeLabel(roomType, t);
        const tooltip = buildRoomTypeTooltip(roomType, t);
        const roomTypeMeta = accommodationMeta?.roomType?.[roomType];

        return {
            content: (
                <Checkbox
                    key={roomType}
                    input={{
                        id: `cb${roomType}`,
                        value: roomType,
                        name: `cb${roomType}`,
                    }}
                    isChecked={isChecked}
                    onChange={() => onSelect(roomType)}
                    withUXSelection
                    customClass={{
                        Wrapper: classNames({
                            [styles.Changed]: isFilterChanged(roomType),
                        }),
                    }}
                >
                    <FilterLabel
                        customClassList={{ Label: styles.FilterLabel }}
                        labelText={label}
                        tooltip={tooltip}
                        {...roomTypeMeta}
                    />
                </Checkbox>
            ),
            isSelected: isChecked,
            filterValue: label,
        };
    });

    return (
        <Segment
            headline={{ text: t('TRANS-room') }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_RoomType}
            onDelete={() => {
                onClear(() => [...roomTypeList]);
                setRoomTypeList(new Set());
            }}
            filterList={filterList}
            overflowThreshold={
                isSticky
                    ? roomTypeArray.length
                    : config.overflowThreshold.default
            }
        />
    );
};

export default RoomType;
